import {
  BrandsInterface,
  CatalogInterface,
  CategoryInterface,
  ModelInterface,
  ProductDraftRequestPayload,
} from '@interfaces/models/deposit';
import DepositService from '@services/deposit-service';
import React, { useState, useCallback, useMemo, useEffect, FCWithChildren } from 'react';
import { createContext } from 'use-context-selector';
import { useRouter } from 'next/router';
import { usePreferences } from '@context/preferences.context';
import subCategories from '@components/organisms/form-sentence/categories.json';
import { JEWELRY_CATEGORY_IDS, UNSIGNED_BRAND_ID } from '@components/brand-search/brand-search-presenter';
import { accentFold } from '@helpers/utils/general';
import logger from '@helpers/utils/logger/client';
import { useQueries, useQuery } from '@tanstack/react-query';
import { ListContextQueryKeys } from '@enums/react-query-keys';

type ListingOrigin = 'listing_form' | 'pricing_calculator';

export type ListingContextValueType = {
  // State
  isJewelry: boolean;
  brands: BrandsInterface[];
  brandsByFilter: BrandsInterface[];
  universes: CatalogInterface[];
  categories: CategoryInterface[];
  models: ModelInterface[];
  pendingAction: boolean;
  brandSearchTerm: string;
  listingDraftPayload: ProductDraftRequestPayload;
  universeLabel: string;
  categoryLabel: string;
  brandLabel: string;
  modelLabel: string;
  modelVariationLabel: string;
  pricingCalculatorInputTouched: boolean;
  showDefaultPricingCalculatorMessage: boolean;
  // Methods
  setShowDefaultPricingCalculatorMessage: React.Dispatch<React.SetStateAction<boolean>>;
  setPricingCalculatorInputTouched: React.Dispatch<React.SetStateAction<boolean>>;
  setBrandSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  setListingDraftAttribute: (data: Partial<ProductDraftRequestPayload>) => void;
  createListingDraft: (origin: ListingOrigin) => Promise<{ draftId?: string; error?: string }>;
};

const pricingCalculatorpaths = ['/sell/pricing-calculator'];

const sellRoutes = ['/sell/pricing-calculator', '/sell/catalog-management', '/sell-clothes-online'];

const sortInputFirst = (input: string, data: BrandsInterface[]) => {
  const first: BrandsInterface[] = [];
  const others: BrandsInterface[] = [];
  for (let i = 0; i < data.length; i++) {
    if (data[i].name.toLowerCase().indexOf(input) === 0) {
      first.push(data[i]);
    } else {
      others.push(data[i]);
    }
  }
  first.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  others.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  return first.concat(others);
};

export const getBrandsFilter = (
  brands: BrandsInterface[],
  brandSearchTerm: string,
  unsignedBrand: BrandsInterface[],
  isJewelry: boolean,
) => {
  let filteredBrands = brands?.filter((item) =>
    accentFold(item.name.toLowerCase()).includes(accentFold(brandSearchTerm.toLowerCase())),
  );
  if (!filteredBrands.length && !isJewelry) {
    return unsignedBrand;
  }
  filteredBrands = sortInputFirst(brandSearchTerm.toLowerCase(), filteredBrands);
  if (isJewelry) {
    filteredBrands = filteredBrands.filter((brand) => brand.id !== unsignedBrand[0].id);
  }
  return filteredBrands.slice(0, 100) || [];
};

export function getIsJewelry(category: string, universes: CatalogInterface[]) {
  if (JEWELRY_CATEGORY_IDS.includes(category)) {
    return true;
  }
  let jewelryCategories = [];
  for (let i = 0; i < universes.length; i++) {
    for (let j = 0; j < universes[i].categories.length; j++) {
      const uniCat = universes[i].categories[j];
      if (JEWELRY_CATEGORY_IDS.includes(uniCat.id) && uniCat.subCategories) {
        jewelryCategories = jewelryCategories.concat(uniCat.subCategories.map((subCat) => subCat.id));
      }
    }
  }
  return jewelryCategories.includes(category);
}

const ListingContext = createContext<ListingContextValueType>({} as ListingContextValueType);

const ListingProvider: FCWithChildren = ({ children }) => {
  const path = useRouter().pathname;

  const [pendingAction, setPendingAction] = useState<boolean>(true);
  const [brandSearchTerm, setBrandSearchTerm] = useState<string>('');
  const { language } = usePreferences();

  const [pricingCalculatorInputTouched, setPricingCalculatorInputTouched] = useState<boolean>(false);
  const [showDefaultPricingCalculatorMessage, setShowDefaultPricingCalculatorMessage] = useState<boolean>(true);

  const [{ data: brands = [], isLoading: isBrandsLoading }, { data: universes = [], isLoading: isUniverseLoading }] =
    useQueries({
      queries: [
        {
          queryKey: [ListContextQueryKeys.LIST_BRANDS, path, sellRoutes],
          queryFn: () => DepositService.listBrands(),
          enabled: path !== '/sell/catalog-management' && sellRoutes.includes(path),
          onError: (err) => {
            logger.error(err, 'Error fetching listBrands. Server responded with : ');
          },
          initialData: [],
        },
        {
          queryKey: [ListContextQueryKeys.LIST_CATALOG, path, sellRoutes],
          queryFn: () => DepositService.listCatalog(),
          enabled: path !== '/sell/catalog-management' && sellRoutes.includes(path),
          onError: (err) => {
            logger.error(err, 'Error fetching listCatalog. Server responded with : ');
          },
          initialData: [],
        },
      ],
    });

  useEffect(() => {
    if (!isBrandsLoading && !isUniverseLoading) {
      setPendingAction(false);
    }
  }, [isBrandsLoading, isUniverseLoading]);

  const [listingDraftPayload, setListingDraftPayload] = useState<ProductDraftRequestPayload>({
    universe: '1',
    brand: '',
    category: '',
  } as ProductDraftRequestPayload);

  const selectedLanguage = language?.toUpperCase();
  const mappedCategories = subCategories[selectedLanguage] || subCategories.EN;

  const categories = useMemo(() => {
    const data = universes?.filter((i) => i.id === listingDraftPayload.universe)[0]?.categories;
    /** Map categories with JSON subcategories
     * to dislay on the menu the same cat name than on the label */
    const formattedData = data?.map((cat) => {
      const subCategory = cat?.subCategories?.map((subcat) => {
        let s = subcat;
        if (mappedCategories[subcat.id]) {
          s = { ...subcat, ...mappedCategories[subcat.id] };
        }
        return s;
      });
      cat.subCategories = subCategory;
      return cat;
    });
    return formattedData || [];
  }, [listingDraftPayload.universe, universes]);

  const universeLabel = useMemo(() => {
    return universes?.find((universe) => universe.id === listingDraftPayload.universe)?.title || '';
  }, [listingDraftPayload.universe, universes]);

  const categoryLabel = useMemo(() => {
    return mappedCategories[listingDraftPayload.category]?.displayValue || '';
  }, [listingDraftPayload.category, categories]);

  const brandLabel = useMemo(() => {
    return brands?.find((brand) => brand.pimId === listingDraftPayload.brand)?.name || '';
  }, [listingDraftPayload.brand, brands]);

  const legacyBrandId = useMemo(
    () => brands.find((i) => i.pimId === listingDraftPayload.brand)?.id,
    [brands, listingDraftPayload.brand],
  );
  const { data: models = [] } = useQuery<ModelInterface[]>({
    queryKey: [
      ListContextQueryKeys.LIST_MODELS,
      listingDraftPayload.brand,
      legacyBrandId,
      listingDraftPayload.category,
      path,
    ],
    queryFn: () =>
      DepositService.listModels({
        brandId: legacyBrandId,
        categoryId: listingDraftPayload.category,
      }),
    enabled: !!(listingDraftPayload.brand && listingDraftPayload.category && pricingCalculatorpaths.includes(path)),
    initialData: [],
  });

  const modelLabel = useMemo(() => {
    return models?.find((model) => model.id === listingDraftPayload.macroModelId)?.displayName || '';
  }, [listingDraftPayload.macroModelId, models]);

  const modelVariationLabel = useMemo(() => {
    const variations = models?.find((model) => model.id === listingDraftPayload.macroModelId)?.variations[0]?.list;
    if (!variations || !variations?.length) {
      return '';
    }

    return variations?.find((variation) => variation.id === listingDraftPayload.modelVariationId)?.title || '';
  }, [listingDraftPayload.modelVariationId, models]);

  const setListingDraftAttribute = useCallback((data: Partial<ProductDraftRequestPayload>) => {
    const dataKeys = Object.keys(data || {});
    const hasModeldata = dataKeys.includes('macroModelId') || dataKeys.includes('modelVariationId');

    setListingDraftPayload((prev) => {
      const formData = {
        ...prev,
        ...data,
      };

      if (!hasModeldata) {
        formData.macroModelId = '';
        formData.modelVariationId = '';
      }

      return formData;
    });
  }, []);

  const isJewelry = useMemo(
    () => getIsJewelry(listingDraftPayload.category, universes),
    [listingDraftPayload.category, universes],
  );
  const unsignedBrand = useMemo(() => brands?.filter((brand) => brand.id === UNSIGNED_BRAND_ID), [brands]);
  const brandsByFilter = useMemo(
    () => getBrandsFilter(brands, brandSearchTerm, unsignedBrand, isJewelry),
    [brands, brandSearchTerm, unsignedBrand, isJewelry],
  );

  const createListingDraft = useCallback(
    async (origin: ListingOrigin) => {
      setPendingAction(true);
      let draftId: string, error: string;

      const requestData =
        origin === 'listing_form'
          ? {
              ...listingDraftPayload,
              page: listingDraftPayload.category,
            }
          : listingDraftPayload;

      if (origin === 'listing_form') {
        delete requestData.category;
      }

      if (listingDraftPayload.brand !== '185') {
        delete requestData.otherBrand;
      }

      try {
        if (origin === 'listing_form') {
          draftId = await DepositService.createPreduct(requestData);
        } else {
          draftId = await DepositService.createProductDraft({
            attributes: requestData,
          });
        }
      } catch {
        error = 'Error creating the deposit.';
      } finally {
        localStorage.removeItem('deferredActionData');
        setPendingAction(false);
      }

      return {
        draftId,
        error,
      };
    },
    [listingDraftPayload],
  );

  const value = useMemo(
    () => ({
      isJewelry,
      brands,
      brandsByFilter,
      universes,
      categories,
      models,
      pendingAction,
      listingDraftPayload,
      setListingDraftAttribute,
      createListingDraft,
      brandSearchTerm,
      setBrandSearchTerm,
      universeLabel,
      categoryLabel,
      brandLabel,
      modelLabel,
      modelVariationLabel,
      pricingCalculatorInputTouched,
      setPricingCalculatorInputTouched,
      showDefaultPricingCalculatorMessage,
      setShowDefaultPricingCalculatorMessage,
    }),
    [
      isJewelry,
      brands,
      universes,
      categories,
      models,
      pendingAction,
      listingDraftPayload,
      brandSearchTerm,
      brandsByFilter,
      universeLabel,
      categoryLabel,
      brandLabel,
      modelLabel,
      modelVariationLabel,
      pricingCalculatorInputTouched,
      showDefaultPricingCalculatorMessage,
    ],
  );

  return <ListingContext.Provider value={value}>{children}</ListingContext.Provider>;
};

export { ListingContext, ListingProvider };
